/**
 * @fileoverview Org portal — branding settings (Spec 11, co-brand). Edits the
 * org logo, display name, email from-name, and accent color. Saving refreshes
 * the shell so the sidebar re-brands live.
 *
 * The logo is the one field with its own endpoints rather than a slot in the
 * form: it is a PNG upload, it takes effect the moment it lands, and it is
 * therefore never half-edited alongside an unsaved name. That also keeps the
 * Save button from being able to clear a logo by submitting an empty URL.
 *
 * Also hosts the custom-domain setup (O11) rather than giving it a page of its
 * own: an admin who wants their name on the booking page is the same admin who
 * wants their name in the URL, and they arrive here looking for both.
 */

import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { OrgAuthContext } from "@/App";
import orgApi, { getOrgErrorMessage } from "@/lib/orgApi";
import { Card, SectionHeader, Button, Icon } from "@/components/studio/primitives";

const HEX_RE = /^#([0-9a-fA-F]{6})$/;

/** Matches the server's cap, so an obviously oversized file never leaves here. */
const MAX_LOGO_BYTES = 1024 * 1024;

function CustomDomainCard() {
  const [state, setState] = useState(null);
  const [locked, setLocked] = useState(false);
  const [domain, setDomain] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await orgApi.get("/org/domain");
      setState(data);
      setDomain(data.domain || "");
    } catch (err) {
      if (err.response?.status === 403) setLocked(true);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (path) => {
    setBusy(true);
    try {
      const { data } = await orgApi.post(path, path === "/org/domain" ? { domain } : undefined);
      setState(data);
      if (data.status === "active") toast.success("Your domain is live.");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't update your domain"));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = async () => {
    setBusy(true);
    try {
      await orgApi.delete("/org/domain");
      setState({ domain: null, status: null });
      setDomain("");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't disconnect your domain"));
    } finally {
      setBusy(false);
    }
  };

  if (locked) return null;

  return (
    <Card className="mt-6">
      <SectionHeader>Your own web address</SectionHeader>
      <p className="mb-4 font-sans text-[14px] text-[var(--text-secondary)]">
        Serve your booking and program pages on your own domain instead of ours.
      </p>

      {state?.status === "active" ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="font-mono text-[14px] text-[var(--text-primary)]" data-testid="domain-active">
            {state.domain} — live
          </p>
          <Button variant="bare" onPress={disconnect} disabled={busy} data-testid="domain-disconnect">
            Disconnect
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <input
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="music.your-academy.org"
            data-testid="domain-input"
            className="rounded-xl border border-[var(--studio-divider-strong)] bg-[var(--studio-interactive)] px-3 py-2 font-sans text-sm text-[var(--text-primary)]"
          />
          <div className="flex gap-3">
            <Button onPress={() => submit("/org/domain")} disabled={busy || !domain} data-testid="domain-save">
              Use this domain
            </Button>
            {state?.domain ? (
              <Button variant="ghost" onPress={() => submit("/org/domain/verify")} disabled={busy} data-testid="domain-verify">
                Check it
              </Button>
            ) : null}
          </div>
          {state?.steps ? (
            <ol className="mt-2 flex list-decimal flex-col gap-1 pl-5 font-sans text-[13px] text-[var(--text-tertiary)]">
              {state.steps.map((step) => (
                <li key={step}>{step}</li>
              ))}
            </ol>
          ) : null}
          {state?.reason ? (
            <p className="font-sans text-[13px] text-[var(--state-warning)]" data-testid="domain-reason">
              {state.reason} DNS can take a few hours — try again shortly.
            </p>
          ) : null}
        </div>
      )}
    </Card>
  );
}

/**
 * The logo well: preview, upload, remove. Its own endpoints (POST/DELETE
 * `/org/branding/logo`), so a logo lands or leaves on its own rather than
 * riding along with the Save button.
 */
function LogoField({ logoUrl, onChanged }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    // Both checks are re-run server-side; they are here so the common mistakes
    // are answered instantly instead of after a megabyte of upload.
    if (file.type !== "image/png" && !file.name.toLowerCase().endsWith(".png")) {
      toast.error("Please choose a PNG file.");
      return;
    }
    if (file.size > MAX_LOGO_BYTES) {
      toast.error("That file is over 1 MB. Please choose a smaller PNG.");
      return;
    }
    const body = new FormData();
    body.append("file", file);
    setBusy(true);
    try {
      await orgApi.post("/org/branding/logo", body);
      await onChanged?.();
      toast.success("Logo uploaded");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't upload that logo"));
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await orgApi.delete("/org/branding/logo");
      await onChanged?.();
      toast.success("Logo removed");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't remove that logo"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-wrap items-center gap-4">
      {/* The same two tiles the shell uses, so this preview IS the corner. */}
      <div
        className={[
          "flex h-[64px] w-[64px] shrink-0 items-center justify-center overflow-hidden rounded-[14px]",
          "border border-[var(--studio-divider-strong)]",
          logoUrl ? "bg-white p-1.5" : "bg-black", // brand-lint-ignore: matches the shell's logo tile
        ].join(" ")}
      >
        {logoUrl ? (
          <img src={logoUrl} alt="Your logo" data-testid="org-logo-preview" className="h-full w-full object-contain" />
        ) : (
          <Icon name="users" size={26} strokeWidth={2} className="text-[var(--accent-yellow)]" />
        )}
      </div>

      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/png,.png"
            className="hidden"
            data-testid="org-logo-input"
            onChange={(e) => upload(e.target.files?.[0])}
          />
          <Button
            variant="ghost"
            onPress={() => inputRef.current?.click()}
            disabled={busy}
            icon={busy ? "loader" : "upload"}
            data-testid="org-logo-upload"
          >
            {logoUrl ? "Replace logo" : "Upload a PNG"}
          </Button>
          {logoUrl ? (
            <Button variant="bare" onPress={remove} disabled={busy} data-testid="org-logo-remove">
              Remove
            </Button>
          ) : null}
        </div>
        <p className="font-sans text-[12px] text-[var(--text-tertiary)]">
          A square PNG under 1 MB. Transparent backgrounds welcome.
        </p>
      </div>
    </div>
  );
}

export default function OrgBrandingPage() {
  const { org, refresh } = useContext(OrgAuthContext) || {};
  const b = org?.branding || {};
  const [form, setForm] = useState({
    display_name: b.display_name || "",
    email_from_name: b.email_from_name || "",
    accent_color: b.accent_color || "#f4cf08",
  });
  const [saving, setSaving] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (form.accent_color && !HEX_RE.test(form.accent_color)) {
      toast.error("Accent color must be a hex value like #F4CF08");
      return;
    }
    setSaving(true);
    try {
      // logo_url is deliberately absent: the uploader owns it, and sending an
      // empty one here would quietly delete a logo while saving a name.
      await orgApi.put("/org/branding", {
        display_name: form.display_name.trim(),
        email_from_name: form.email_from_name.trim(),
        accent_color: form.accent_color,
      });
      await refresh?.();
      toast.success("Branding saved");
    } catch (err) {
      toast.error(getOrgErrorMessage(err, "Couldn't save branding"));
    } finally {
      setSaving(false);
    }
  };

  const field = "w-full rounded-[12px] border border-[var(--studio-divider-strong)] bg-[var(--studio-raised)] px-4 py-[11px] font-sans text-[15px] text-[var(--text-primary)] outline-none placeholder:text-[var(--text-tertiary)] focus:border-[var(--accent-yellow)]";
  const label = "mb-1.5 block font-sans text-[11px] font-semibold uppercase tracking-[0.06em] text-[var(--text-tertiary)]";

  return (
    <div>
      <h1 className="mb-6 font-serif text-2xl font-semibold text-[var(--text-primary)]">Branding</h1>

      <Card className="mb-6">
        <SectionHeader>How your organization appears</SectionHeader>
        <p className="mb-5 font-sans text-[13px] text-[var(--text-tertiary)]">
          Your logo and name sit in the top-left corner of this portal. With co-branding they also reach your
          instructors' app shell, their booking pages, and the lesson emails they send, alongside your accent color.
          Hans keeps a small "Powered by Hans" line.
        </p>

        <div className="flex flex-col gap-4">
          <div>
            <label className={label}>Display name</label>
            <input className={field} value={form.display_name} data-testid="org-brand-name"
              onChange={(e) => set("display_name", e.target.value)} placeholder="Maestro Music Professionals" />
          </div>

          <div>
            <label className={label}>Email from-name</label>
            <input className={field} value={form.email_from_name}
              onChange={(e) => set("email_from_name", e.target.value)} placeholder="Maestro Music" />
            <p className="mt-1 font-sans text-[12px] text-[var(--text-tertiary)]">The sender name on emails Hans sends for you: lesson notes to families, and notices to your instructors.</p>
          </div>

          <div>
            <label className={label}>Logo</label>
            <LogoField logoUrl={b.logo_url || null} onChanged={refresh} />
          </div>

          <div>
            <label className={label}>Accent color</label>
            <div className="flex items-center gap-3">
              <input type="color" value={HEX_RE.test(form.accent_color) ? form.accent_color : "#f4cf08"}
                onChange={(e) => set("accent_color", e.target.value)}
                className="h-10 w-14 cursor-pointer rounded-[10px] border border-[var(--studio-divider-strong)] bg-transparent" />
              <input className={field + " max-w-[160px]"} value={form.accent_color} data-testid="org-brand-accent"
                onChange={(e) => set("accent_color", e.target.value)} placeholder="#F4CF08" />
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onPress={save} disabled={saving} icon={saving ? "loader" : "check"} data-testid="org-brand-save">
          {saving ? "Saving…" : "Save branding"}
        </Button>
      </div>

      <CustomDomainCard />
    </div>
  );
}
